import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MessageCircle, Users2, Eye, EyeOff, Send, Copy, Check, User } from "lucide-react";
import { buildLessonMessage, participantsOf } from "@/lib/whatsapp-templates";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function buildWaLink(phone, text) {
  const digits = (phone || "").replace(/[^\d]/g, "");
  const withCountry = digits.startsWith("34") ? digits : `34${digits}`;
  return `https://wa.me/${withCountry}?text=${encodeURIComponent(text)}`;
}

/**
 * WhatsApp message preview + send dialog.
 *
 * Given an appointment (main + shared participants), shows a personalised
 * message for the selected viewer. If identity is hidden the co-participants
 * remain anonymous. The button opens wa.me directly.
 */
export default function WhatsAppPreviewDialog({
  open,
  appointment,
  franchise = "Natur Autoescuela",
  onClose,
  onSent,
}) {
  const participants = useMemo(
    () => (appointment ? participantsOf(appointment) : []),
    [appointment]
  );
  const [viewerId, setViewerId] = useState("");
  const [copied, setCopied] = useState(false);
  const [globalHide, setGlobalHide] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Default to main student
    const main = participants.find((p) => p.is_main) || participants[0];
    setViewerId(main?.student_id || "");
    setCopied(false);
    // Load the global identity-hide default (fresh each open — cheap and always up to date).
    (async () => {
      try {
        const { data } = await api.get("/settings/hide_shared_identity_default");
        setGlobalHide((data?.value || "false").toLowerCase() === "true");
      } catch (_) { setGlobalHide(false); }
    })();
  }, [open, participants]);

  if (!appointment) return null;

  // Resolve effective identity toggle: appointment override wins over global
  const hideIdentity =
    appointment.hide_shared_identity !== null && appointment.hide_shared_identity !== undefined
      ? !!appointment.hide_shared_identity
      : !!globalHide;

  const toggleAppointmentOverride = async (value) => {
    setSavingOverride(true);
    try {
      await api.patch(`/appointments/${appointment.id}/hide-shared-identity`, {
        hide_shared_identity: value,
      });
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSavingOverride(false);
    }
  };

  const viewer = participants.find((p) => p.student_id === viewerId) || participants[0];
  const vehicle =
    appointment.vehicle && appointment.vehicle.plate
      ? { plate: appointment.vehicle.plate }
      : null;

  const message = viewer
    ? buildLessonMessage({
        viewer,
        participants,
        appointment,
        vehicle,
        hideIdentity,
        franchise,
      })
    : "";

  const send = async () => {
    if (!viewer?.phone) {
      toast.error("Este alumno no tiene teléfono registrado");
      return;
    }
    window.open(buildWaLink(viewer.phone, message), "_blank", "noopener,noreferrer");
    // Mark the main student's whatsapp_sent flag when the main viewer is used.
    if (viewer.is_main) {
      try {
        await api.patch(`/appointments/${appointment.id}`, { whatsapp_sent: true });
        onSent?.();
      } catch (_) { /* silent */ }
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      toast.error("No se pudo copiar el mensaje");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="wa-preview-dialog">
        <div className="bg-gradient-to-br from-emerald-50 to-white px-5 pt-5 pb-3 border-b border-emerald-100">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-500 flex items-center justify-center text-white shadow-sm">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div>
              <DialogHeader className="p-0 space-y-0">
                <DialogTitle className="text-base font-bold text-neutral-900">
                  Vista previa de WhatsApp
                </DialogTitle>
                <DialogDescription className="text-xs text-neutral-600 flex items-center gap-2">
                  <span>Mensaje personalizado por participante</span>
                  {participants.length > 1 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 text-[10px] font-semibold px-2 py-0.5">
                      <Users2 className="h-3 w-3" /> Sesión compartida
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Viewer selector */}
          <div className="flex flex-wrap gap-1.5">
            {participants.map((p, idx) => {
              const active = p.student_id === viewerId;
              return (
                <button
                  key={p.student_id}
                  type="button"
                  onClick={() => setViewerId(p.student_id)}
                  data-testid={`wa-viewer-${p.student_id}`}
                  className={
                    "inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-semibold transition-colors border " +
                    (active
                      ? "bg-neutral-900 text-white border-neutral-900"
                      : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400")
                  }
                >
                  <User className="h-3 w-3" />
                  Ver mensaje Alumno {idx + 1}
                  {p.is_main && <span className="opacity-70 text-[10px] ml-1">(titular)</span>}
                </button>
              );
            })}
          </div>

          {/* Identity indicator + per-appointment override */}
          <div
            data-testid="wa-identity-indicator"
            className={
              "flex items-center gap-2 text-xs rounded-lg border px-3 py-2 flex-wrap " +
              (hideIdentity
                ? "border-neutral-300 bg-neutral-50 text-neutral-700"
                : "border-emerald-200 bg-emerald-50 text-emerald-800")
            }
          >
            {hideIdentity ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            <span>
              {hideIdentity
                ? "Identidad de los otros participantes oculta."
                : "El mensaje incluye los nombres de los otros participantes."}
            </span>
            <span className="text-[10px] text-neutral-500 ml-1">
              (global: {globalHide ? "oculta" : "visible"})
            </span>
            {appointment.hide_shared_identity !== null && appointment.hide_shared_identity !== undefined && (
              <span className="rounded-full bg-white/70 border border-neutral-300 px-2 py-0.5 text-[10px] font-semibold">
                override
              </span>
            )}
            {participants.length > 1 && (
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => toggleAppointmentOverride(hideIdentity ? null : true)}
                  disabled={savingOverride}
                  data-testid="wa-override-hide"
                  className={
                    "rounded-md text-[10px] font-semibold px-2 py-1 border transition-colors " +
                    (hideIdentity && appointment.hide_shared_identity === true
                      ? "bg-neutral-900 text-white border-neutral-900"
                      : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500")
                  }
                >
                  Ocultar en esta cita
                </button>
                <button
                  type="button"
                  onClick={() => toggleAppointmentOverride(!hideIdentity ? null : false)}
                  disabled={savingOverride}
                  data-testid="wa-override-show"
                  className={
                    "rounded-md text-[10px] font-semibold px-2 py-1 border transition-colors " +
                    (!hideIdentity && appointment.hide_shared_identity === false
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500")
                  }
                >
                  Mostrar en esta cita
                </button>
                {appointment.hide_shared_identity !== null && appointment.hide_shared_identity !== undefined && (
                  <button
                    type="button"
                    onClick={() => toggleAppointmentOverride(null)}
                    disabled={savingOverride}
                    data-testid="wa-override-clear"
                    className="rounded-md text-[10px] font-semibold px-2 py-1 border border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50"
                  >
                    Usar global
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Message preview — mimics a WhatsApp bubble */}
          <div
            data-testid="wa-message-body"
            className="rounded-2xl bg-[#DCF8C6] text-neutral-900 px-4 py-3 whitespace-pre-wrap text-[13px] leading-relaxed shadow-inner"
            style={{ fontFamily: "ui-sans-serif, system-ui" }}
          >
            {message}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="text-[11px] text-neutral-500">
              Destinatario: <span className="font-semibold text-neutral-700">{viewer?.name}</span>
              {viewer?.phone ? (
                <span className="ml-2 font-mono text-neutral-600">+34 {viewer.phone}</span>
              ) : (
                <span className="ml-2 text-red-600 font-semibold">Sin teléfono</span>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={copy}
                className="btn-secondary !h-9 !text-xs"
                data-testid="wa-copy"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copiado" : "Copiar"}
              </button>
              <button
                onClick={send}
                disabled={!viewer?.phone}
                className="btn-whatsapp !h-9 !text-xs"
                data-testid="wa-send"
              >
                <Send className="h-3.5 w-3.5" />
                Enviar por WhatsApp
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
